import { Calendar, Check, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BOOKING_URL_BEAUTY = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const treatmentAreas = [
  { area: 'Upper Lip', price: 'From $50' },
  { area: 'Chin', price: 'From $60' },
  { area: 'Underarms', price: 'From $80' },
  { area: 'Bikini', price: 'From $100' },
  { area: 'Brazilian', price: 'From $150' },
  { area: 'Half Leg', price: 'From $180' },
  { area: 'Full Leg', price: 'From $280' },
  { area: 'Full Arm', price: 'From $150' },
];

const benefits = [
  { title: 'Painless Technology', description: 'Our advanced laser technology makes treatments virtually pain-free' },
  { title: 'Safe for All Skin Types', description: 'Effective and safe for light to dark skin tones' },
  { title: 'Fast Treatments', description: 'Quick sessions that fit into your busy schedule' },
  { title: 'Long-lasting Results', description: 'Permanent hair reduction with a course of treatments' },
  { title: 'No Downtime', description: 'Return to your daily activities immediately' },
  { title: 'Expert Technicians', description: 'Fully trained and experienced laser specialists' },
];

const faqs = [
  {
    q: 'How many sessions will I need?',
    a: 'Most clients see optimal results after 6-8 sessions, spaced 4-6 weeks apart. The number of sessions depends on your hair type, colour, and the treatment area.'
  },
  {
    q: 'Is laser hair removal permanent?',
    a: 'Laser hair removal provides permanent hair reduction. Most clients experience 80-90% reduction after completing their treatment course, with occasional maintenance sessions.'
  },
  {
    q: 'Does it hurt?',
    a: 'Our advanced technology makes treatments virtually painless. Most clients describe it as a slight warming sensation. It\'s much more comfortable than waxing!'
  },
  {
    q: 'Can all skin types be treated?',
    a: 'Yes! Our laser technology is safe and effective for all skin types, from very fair to dark skin tones.'
  }
];

export default function LaserHairRemoval() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="laser-hair-removal-page">
      <SEO page="laserHairRemoval" />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1519699047748-de8e457a634e?w=1920&h=800&fit=crop"
            alt="Laser hair removal Camberwell - smooth skin"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/80" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#D4C4B0]/20 border border-[#D4C4B0]/50 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-[#8D7B68]" />
              <span className="text-sm text-[#5D4A3A]">Advanced Laser Technology</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              Painless Laser Hair Removal Camberwell
            </h1>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              Silky smooth skin with painless laser hair removal in Camberwell. Our advanced technology is safe for all skin types. Effective, fast, and professional treatments at Artistry House Beauty, located just off Burke Road near Camberwell Junction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-8 py-4 text-lg">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Laser Consultation
                </Button>
              </a>
              <Link to="/beauty">
                <Button variant="outline" className="border-[#3D352D] text-[#3D352D] hover:bg-[#3D352D] hover:text-white rounded-full px-8 py-4 text-lg">
                  View All Beauty Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#4A5D44]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center text-white">
            {benefits.map((benefit) => (
              <div key={benefit.title}>
                <p className="font-medium mb-1">{benefit.title}</p>
                <p className="text-xs text-white/70">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Areas & Pricing */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Treatment Areas</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Laser Hair Removal Pricing</h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              We offer laser hair removal for all body areas. Package deals available for multiple areas.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {treatmentAreas.map((item) => (
              <div key={item.area} className="bg-white p-6 rounded-lg border border-[#D4C4B0]/50 text-center hover:border-[#4A5D44]/50 transition-colors">
                <h3 className="font-medium text-[#2C2420] mb-2">{item.area}</h3>
                <p className="text-[#4A5D44] font-medium">{item.price}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-[#6B5D4D]">* Per session pricing. Package discounts available for course bookings.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/50">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#4A5D44] mb-4">The Process</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                How Laser Hair Removal Works
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
                Laser hair removal uses concentrated light energy to target hair follicles. The pigment in the hair absorbs the light, which damages the follicle and inhibits future hair growth.
              </p>
              <ol className="space-y-4">
                {[
                  { step: '1', title: 'Consultation', desc: 'We assess your skin and hair type to create a personalized treatment plan' },
                  { step: '2', title: 'Preparation', desc: 'Shave the treatment area 24 hours before your appointment' },
                  { step: '3', title: 'Treatment', desc: 'Quick, comfortable treatment with our advanced laser technology' },
                  { step: '4', title: 'Aftercare', desc: 'Simple aftercare instructions for best results' },
                ].map((item) => (
                  <li key={item.step} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#4A5D44] text-white flex items-center justify-center flex-shrink-0 font-medium">
                      {item.step}
                    </div>
                    <div>
                      <p className="font-medium text-[#2C2420]">{item.title}</p>
                      <p className="text-sm text-[#5D4A3A]">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div className="aspect-square rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=600&h=600&fit=crop"
                alt="Laser hair removal treatment Camberwell"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Common Questions</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Laser Hair Removal FAQs</h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white p-6 rounded-lg border border-[#D4C4B0]/50">
                <h3 className="text-lg font-medium text-[#2C2420] mb-2">{faq.q}</h3>
                <p className="text-[#5D4A3A]">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#2C2420]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-medium text-white mb-6">
            Ready for Silky Smooth Skin?
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-2xl mx-auto mb-8">
            Book a consultation at our Camberwell salon. Our laser specialists will create a treatment plan tailored to your goals.
          </p>
          <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-10 py-6 text-lg">
              <Calendar className="w-5 h-5 mr-2" />
              Book Laser Consultation
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
